import { ModuleIds } from './../../shared/permission-constants/permission-const';
import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { PurchaseService } from '../../features/purchase/services/purchase.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ToastrService } from 'ngx-toastr';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { PermissionService } from '../../shared/services/permission.service';


@Component({
  selector: 'app-daily-purchase',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
  ],
  templateUrl: './purchase.component.html',
  styleUrl: './purchase.component.scss',
})
export class PurchaseComponent implements OnInit {
  public viewPortSize: boolean = false;
  public loading: boolean = true;
  public error: any = null;
  readonly dialog = inject(MatDialog);
  columnDefs: any[] = [];
  public rowData : any[] = [];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10];
  public themeClass: string = 'ag-theme-quartz';

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private purchaseService = inject(PurchaseService);
  private toastr = inject(ToastrService);
  canCreateDailyPurchase$ = this.permissionService.hasPermission(
    ModuleIds.DailyPurchase,
    'Create'
  );
  canEditDailyPurchase$ = this.permissionService.hasPermission(
    ModuleIds.DailyPurchase,
    'Edit'
  );
  canDetailsDailyPurchase$ = this.permissionService.hasPermission(
    ModuleIds.DailyPurchase,
    'Details'
  );
  canDetailsDailyPurchase:boolean = false;
  canEditDailyPurchase:boolean = false;

  constructor(private router: Router,private permissionService: PermissionService) {}

  ngOnInit(): void {
    this.checkViewportSize();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchDailyExpense(searchTerm);
      });
    this.fetchDailyExpense();
    this.canEditDailyPurchase$.subscribe(val => {
      this.canEditDailyPurchase = val;
    });
    this.canDetailsDailyPurchase$.subscribe(val => {
      this.canDetailsDailyPurchase = val;
    });
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.updateColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Supplier Name',
        field: 'supplierName',
        flex: this.viewPortSize ? 1 : 0,
      },
      { headerName: 'Bill No', field: 'code', flex: this.viewPortSize ? 1 : 0 },
      {
        headerName: 'Payment Method',
        field: 'paymentMethod',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Total',
        field: 'total',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];

    this.columnDefs.push({
      field: 'action',
      headerName: 'Actions',
      floatingFilter: false,
      filter: false,
      flex: this.viewPortSize ? 1 : 0,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.gap = '20px';

        const editIcon = document.createElement('img');
        editIcon.src = 'assets/images/icons/icon-edit.svg';
        editIcon.style.cursor = 'pointer';
        editIcon.setAttribute('data-action', 'edit');
        if(this.canEditDailyPurchase){
          container.appendChild(editIcon);
        }

        // const deleteIcon = document.createElement('img');
        // deleteIcon.src = 'assets/images/icons/icon-delete.svg';
        // deleteIcon.style.cursor = 'pointer';
        // deleteIcon.setAttribute('data-action', 'delete');
        // container.appendChild(deleteIcon);

        return container;
      },
    });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  onCellClicked(params: any) {
    const cellData = params.data;
    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;
      console.log('action', action);

      if (action === 'edit') {
        this.router.navigate([
          `daily-purchase/purchase-edit/${cellData?.id}`,
        ]);
      }
       else if (action === 'delete') {
        this.onDelete(cellData);
      }
    }
    else if(this.canDetailsDailyPurchase){
      this.router.navigate([
        `daily-purchase/purchase-details/${cellData?.id}`,
      ]);
    }
  }

  fetchDailyExpense(search = '') {
    this.purchaseService.fetchDailyExpense(search).subscribe({
      next: (data) => {
        this.rowData = data.map((dailyExpense: any) => ({
          id: dailyExpense?.id,
          supplierName: dailyExpense?.supplierData?.supplierName || '--',
          paymentMethod: dailyExpense?.paymentMethod || '--',
          total: `AED ${parseFloat(dailyExpense.total).toFixed(2)}`,
          code: dailyExpense?.dailyExpenseNo || '--',
        }));
        this.paginationPageSizeSelector = PaginationHelper.getPaginationPageSizeOptions(this.rowData);
      },
      error: (error) => {
        console.error('Error fetching daily expense:', error);
      },
    });
  }

   onDelete(data: any) {
      const expenseId = data.id;
      if (!expenseId) return;
      const dialogRef = this.dialog.open(ConfirmDialog, {
        data: {
          title: 'Confirm Deletion',
          message: `Are you sure you want to delete Expense ?`,
          confirmText: 'Delete',
        },
      });
  
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.purchaseService.deleteDailyExpense(expenseId).subscribe({
            next: () => {
              this.toastr.success('Expense deleted successfully!', 'Success');
              this.fetchDailyExpense();
            },
            error: (error) => {
              this.toastr.error(`Failed to delete Expense`);
              console.error('Deletion error:', error);
            },
          });
        }
      });
    }

    onSearch(event: Event) {
      const input = event.target as HTMLInputElement;
      const searchTerm = input.value;
      this.searchSubject.next(searchTerm);
    }
  
}
